// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { type AdvisoryBank } from '@shared/data-access/types/brokerage-types';
import { composeEventHandler, formatRelativeTime } from '@shared/ui-kit';
import { useMemo, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';

export interface UseInstitutionSyncStatusProps {
  readonly institution: AdvisoryBank;
  readonly onSelectInstitution?: (institution: AdvisoryBank) => void;
  readonly onSyncBank?: (id: string, name: string) => void;
}

export const useInstitutionSyncStatus = ({
  institution,
  onSelectInstitution,
  onSyncBank,
}: UseInstitutionSyncStatusProps) => {
  const { i18n, t } = useTranslation();

  const lastSyncFormatted = useMemo(
    () =>
      formatRelativeTime(institution.last_sync, {
        locale: i18n.language,
        style: 'long',
        direction: 'past',
      }),
    [i18n.language, institution.last_sync],
  );

  const onSync = composeEventHandler(
    (_: MouseEvent<HTMLButtonElement>) =>
      onSyncBank?.(institution.id, institution.name),
    {
      stopPropagation: true,
    },
  );

  const onClick = composeEventHandler(
    (_: MouseEvent<HTMLButtonElement>) => onSelectInstitution?.(institution),
    {
      stopPropagation: true,
    },
  );

  const errorType = useMemo(() => {
    switch (institution?.error) {
      case undefined:
      case null:
        return 'success';
      case 'reconnect':
        return 'warning';
      case 'ignore':
      case 'retry':
        if (!institution.errorMessage) return 'success';

        return 'error';
    }
  }, [institution?.error, institution.errorMessage]);

  return { onClick, lastSyncFormatted, errorType, onSync, t };
};
